import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  const sendMessage = async (message) => {
    if (!message.trim() || isLoading) return;

    const userMessage = { role: "user", content: message };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: message,
      });

      setSessionId(response.data.session_id);
      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = {
        role: "assistant",
        content:
          "I apologize, but I'm having trouble connecting right now. Please try again in a moment or call us directly.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  const openChat = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      // Initial greeting
      setMessages([
        {
          role: "assistant",
          content:
            "Hi there! 👋 Welcome to Palmetto South Insurance Group. I'm here to help you get a free quote. What type of insurance are you interested in today?",
        },
      ]);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          data-testid="chat-widget-button"
          onClick={openChat}
          className="fixed bottom-24 right-6 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-accent text-white shadow-lg chat-pulse hover:scale-105 transition-transform duration-200"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed bottom-24 right-6 z-[9999] w-[380px] max-w-[calc(100vw-48px)] rounded-2xl bg-white shadow-2xl border border-border overflow-hidden animate-slide-in-right"
        >
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white text-sm">
                  Palmetto Assistant
                </h3>
                <p className="text-xs text-white/80">Here to help with your quote</p>
              </div>
            </div>
            <button
              data-testid="chat-close-button"
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea
            className="h-[350px] p-4 chat-scroll"
          >
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  } chat-bubble-enter`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                      msg.role === "user"
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-secondary text-foreground rounded-bl-md"
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground rounded-full typing-dot"></span>
                    <span className="w-2 h-2 bg-muted-foreground rounded-full typing-dot"></span>
                    <span className="w-2 h-2 bg-muted-foreground rounded-full typing-dot"></span>
                  </div>
                </div>
              )}
            </div>
            <div ref={bottomRef} />

            {/* Quick Reply Buttons - show only at start */}
            {messages.length === 1 && !isLoading && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid="quick-reply-home"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickReply("I need Home Insurance")}
                  className="rounded-full text-xs border-primary text-primary hover:bg-primary hover:text-white"
                >
                  🏠 Home Insurance
                </Button>
                <Button
                  data-testid="quick-reply-auto"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickReply("I need Auto Insurance")}
                  className="rounded-full text-xs border-primary text-primary hover:bg-primary hover:text-white"
                >
                  🚗 Auto Insurance
                </Button>
                <Button
                  data-testid="quick-reply-life"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickReply("I need Life Insurance")}
                  className="rounded-full text-xs border-primary text-primary hover:bg-primary hover:text-white"
                >
                  💚 Life Insurance
                </Button>
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-border">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                data-testid="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 rounded-full border-muted"
                disabled={isLoading}
              />
              <Button
                data-testid="chat-send-button"
                type="submit"
                size="icon"
                disabled={isLoading || !inputValue.trim()}
                className="rounded-full bg-accent hover:bg-accent/90 h-10 w-10"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
